/* eslint-disable no-console */
import * as Sentry from '@sentry/nextjs';
import { parseCookies } from 'nookies';
import React from 'react';
import { UAParser } from 'ua-parser-js';
import { useUserInfoAndMenus } from '../../hooks/useUserInfoAndMenus';
import type {
  ClientKey,
  ClickD0LogBody,
  SessionInfo,
  ViewLogBody,
} from './types';
import type { Config } from 'u-next/config';

async function sendBatch({
  apiEndpoint,
  data,
}: {
  apiEndpoint: string;
  data: Record<string, unknown>;
}) {
  try {
    console.log('send', data);

    await fetch(`${apiEndpoint}/api/v2`, {
      method: 'POST',
      credentials: 'include',
      cache: 'no-cache',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  } catch (error) {
    console.error('send error', error);
    Sentry.captureException(error);
  }
}

function getDeviceInfo() {
  const parser = new UAParser();
  const result = parser.getResult();

  return {
    app_version: process.env.COMMIT_TAG ?? '',
    os: result.os.name || '',
    os_version: result.os.version || '',
    user_agent: result.ua || '',
    viewport_h: typeof window !== 'undefined' ? window.innerHeight : 0,
    viewport_w: typeof window !== 'undefined' ? window.innerWidth : 0,
    model_name: result.device.model || '',
    browser_name: result.browser.name || '',
    browser_version: result.browser.version || '',
  };
}

function mapUserPlatformCode(
  service_platform: ClientKey['service_platform']
): ClientKey['service_platform'] {
  switch (service_platform) {
    case 'bos':
    case 'BOS':
      return 'BOS';

    case 'unext':
    case 'nsbo':
      return 'NSBO';

    case 'yamada':
    case 'YAMADA':
      return 'YAMADA';

    case 'gelatoni':
      return 'GELATONI';

    case 'amazon':
    default:
      return '';
  }
}

export function useSuperFlowClient({
  SUPER_FLOW: { API_ENDPOINT, DATA_SET_UNEXT_LOG, DATA_SET_UNEXT_MISC },
  KAFKA_LOG_VERSION,
}: Config) {
  const { isLoadedUserInfo, userInfo } = useUserInfoAndMenus();

  const clientKey = React.useMemo<ClientKey>(() => {
    if (!isLoadedUserInfo) {
      return {
        service_platform: '',
        client: 'web',
        user_multi_account_id: '',
        device_id: '',
      };
    }

    const cookies = parseCookies();
    const tdGlobalId = cookies['_td_global'] ?? '';

    return {
      service_platform: userInfo.isLoggedIn
        ? mapUserPlatformCode(
            userInfo.userPlatformCode as ClientKey['service_platform']
          )
        : '',
      client: 'web',
      user_multi_account_id: userInfo.isLoggedIn
        ? userInfo.userMultiAccountId
        : '',
      device_id: tdGlobalId,
    };
  }, [
    isLoadedUserInfo,
    userInfo.isLoggedIn,
    userInfo.userMultiAccountId,
    userInfo.userPlatformCode,
  ]);

  const sessionInfo = React.useMemo<SessionInfo>(() => {
    const baseSession =
      isLoadedUserInfo && userInfo.isLoggedIn
        ? {
            user_platform_id: userInfo.userPlatformId,
            super_user_flg: userInfo.isSuperUser ? 1 : 0,
            experiment: '',
            alliance_code: userInfo.allianceCode,
          }
        : {
            user_platform_id: '',
            super_user_flg: 0,
            experiment: '',
            alliance_code: '',
          };

    return {
      ...baseSession,
      tracking_id_type: 'td_global_id',
      tracking_id_value: clientKey.device_id,
      tracking_id_perm_stat: 'unknown',
    };
  }, [
    isLoadedUserInfo,
    userInfo.isLoggedIn,
    userInfo.isSuperUser,
    userInfo.userPlatformId,
    userInfo.allianceCode,
    clientKey.device_id,
  ]);

  const trackUserClickLog = React.useCallback(
    <T extends ClickD0LogBody>({
      target,
      detail,
      eventId = '',
    }: {
      target: T['event']['target'];
      detail: T['event']['detail'];
      eventId?: string;
    }) => {
      const timestamp = Date.now();
      return sendBatch({
        apiEndpoint: API_ENDPOINT,
        data: {
          key: clientKey,
          session: sessionInfo,
          device: getDeviceInfo(),
          events: [
            {
              /**
               * eventLog送信先
               **/
              dataset: DATA_SET_UNEXT_LOG,
              table: 'user-ui-interaction',
              /**
               * eventLog本体
               **/
              action: 'click',
              base_schema: 'user_click_dimension_0_default',
              target,
              detail,
              /**
               * eventLog付属情報
               **/
              version: KAFKA_LOG_VERSION,
              timestamp: timestamp,
              event_id: eventId,
            },
          ],
          batch_timestamp: Date.now(),
        },
      });
    },
    [
      API_ENDPOINT,
      DATA_SET_UNEXT_LOG,
      KAFKA_LOG_VERSION,
      clientKey,
      sessionInfo,
    ]
  );

  const trackUserViewLog = React.useCallback(
    <T extends ViewLogBody>({
      target,
      detail,
      eventId = '',
    }: {
      target: T['event']['target'];
      detail: T['event']['detail'];
      eventId?: string;
    }) => {
      const timestamp = Date.now();
      return sendBatch({
        apiEndpoint: API_ENDPOINT,
        data: {
          key: clientKey,
          session: sessionInfo,
          device: getDeviceInfo(),
          events: [
            {
              /**
               * eventLog送信先
               **/
              dataset: DATA_SET_UNEXT_LOG,
              table: 'user-ui-interaction',
              /**
               * eventLog本体
               **/
              action: 'view',
              base_schema: 'user_view_page_default',
              target,
              detail,
              /**
               * eventLog付属情報
               **/
              version: KAFKA_LOG_VERSION,
              timestamp: timestamp,
              event_id: eventId,
            },
          ],
          batch_timestamp: timestamp,
        },
      });
    },
    [
      API_ENDPOINT,
      DATA_SET_UNEXT_LOG,
      KAFKA_LOG_VERSION,
      clientKey,
      sessionInfo,
    ]
  );

  const trackCxHelpFeedbackLog = React.useCallback(
    (
      event:
        | { url: string; action: 'view' }
        | { url: string; action: 'feedback'; helpful: boolean }
    ) => {
      const timestamp = Date.now();
      return sendBatch({
        apiEndpoint: API_ENDPOINT,
        data: {
          key: clientKey,
          session: sessionInfo,
          device: getDeviceInfo(),
          events: [
            {
              /**
               * eventLog送信先
               **/
              dataset: DATA_SET_UNEXT_MISC,
              table: 'cx-help-feedback',
              /**
               * eventLog本体
               **/
              ...event,
              /**
               * eventLog付属情報
               **/
              version: KAFKA_LOG_VERSION,
              timestamp: timestamp,
              event_id: '',
            },
          ],
          batch_timestamp: timestamp,
        },
      });
    },
    [
      API_ENDPOINT,
      DATA_SET_UNEXT_MISC,
      KAFKA_LOG_VERSION,
      clientKey,
      sessionInfo,
    ]
  );

  const trackUserContractLog = React.useCallback(
    (event: { rid: string; lp_code: string; contract_id: string }) => {
      const timestamp = Date.now();
      return sendBatch({
        apiEndpoint: API_ENDPOINT,
        data: {
          key: clientKey,
          session: sessionInfo,
          device: getDeviceInfo(),
          events: [
            {
              /**
               * eventLog送信先
               **/
              dataset: DATA_SET_UNEXT_LOG,
              table: 'user-contract-log',
              /**
               * eventLog本体
               **/
              ...event,
              /**
               * eventLog付属情報
               **/
              version: KAFKA_LOG_VERSION,
              timestamp: timestamp,
              event_id: '',
            },
          ],
          batch_timestamp: timestamp,
        },
      });
    },
    [
      API_ENDPOINT,
      DATA_SET_UNEXT_LOG,
      KAFKA_LOG_VERSION,
      clientKey,
      sessionInfo,
    ]
  );

  return {
    isReadyToSend: isLoadedUserInfo,
    trackCxHelpFeedbackLog,
    trackUserClickLog,
    trackUserViewLog,
    trackUserContractLog,
  };
}
